
import React, { useState } from 'react';
import { CustomerSearch } from './CustomerSearch';
import { ProductSearch } from './ProductSearch';
import { ProductDetail } from './ProductDetail';
import { CheckoutPage } from './CheckoutPage';
import { PaymentProcessing } from './PaymentProcessing';
import { OrderConfirmation } from './OrderConfirmation';
import { ShoppingCart } from './ShoppingCart';
import { CheckoutFlow } from './CheckoutFlow';
import { SalesOrderBooking } from './SalesOrderBooking';
import { useSales } from './hooks/useSales';

export const SalesWorkflow: React.FC = () => {
  // State declarations
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showProductDetail, setShowProductDetail] = useState<string | null>(null);
  const [showPayment, setShowPayment] = useState(false);
  const [orderData, setOrderData] = useState<any>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutData, setCheckoutData] = useState<any>(null);

  // Hook for sales cart management
  const {
    cartItems,
    addToCart,
    clearCart,
    getCartTotals
  } = useSales();

  // Handlers
  const handleCustomerSelect = (customer: any) => {
    setSelectedCustomer(customer);
    if (customer) {
      setCurrentStep(2);
    }
  };

  const handleProductSelect = (product: any) => {
    if (selectedCustomer) {
      addToCart(product, 1);
      // Show success message or update UI
      console.log('Product added to cart:', product.model_name);
    } else {
      alert('Please select a customer first');
    }
  };

  const handleAddToOrder = (product: any, quantity: number) => {
    const existingIndex = selectedProducts.findIndex(p => p.id === product.id);
    if (existingIndex >= 0) {
      const updated = [...selectedProducts];
      updated[existingIndex].quantity = quantity;
      setSelectedProducts(updated);
    } else {
      setSelectedProducts([...selectedProducts, { ...product, quantity }]);
    }
    setShowProductDetail(null);
  };

  const removeProduct = (productId: string) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const updateProductQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeProduct(productId);
      return;
    }
    setSelectedProducts(selectedProducts.map(p =>
      p.id === productId ? { ...p, quantity } : p
    ));
  };

  const proceedToCheckout = () => {
    if (selectedProducts.length > 0) {
      setCurrentStep(3);
    }
  };

  const handleOrderComplete = (orderId: string) => {
    setOrderData({ orderId, customer: selectedCustomer, products: selectedProducts });
    setShowPayment(true);
  };

  const handlePaymentComplete = (payment: any) => {
    setPaymentData(payment);
    setShowPayment(false);
    setCurrentStep(4);
  };

  const startNewSale = () => {
    setCurrentStep(1);
    setSelectedCustomer(null);
    setSelectedProducts([]);
    setOrderData(null);
    setPaymentData(null);
    setShowProductDetail(null);
    setShowPayment(false);
  };

  const handleCartCheckout = (items: any[], totals: any) => {
    setCheckoutData({ items, totals });
    setShowCart(false);
    setShowCheckout(true);
  };

  const handleCheckoutComplete = (orderData: any) => {
    setShowCheckout(false);
    setCheckoutData(null);
    clearCart();
    setCurrentStep(1);
    setSelectedCustomer(null);
    // Show success message
    alert(`Order ${orderData.id} created successfully!`);
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const canProceed = () => {
    if (currentStep === 1) {
      return !!selectedCustomer;
    }
    if (currentStep === 2) {
      return selectedProducts.length > 0 || cartItems.length > 0;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && selectedCustomer) {
      setCurrentStep(2);
    } else if (currentStep === 2 && (selectedProducts.length > 0 || cartItems.length > 0)) {
      proceedToCheckout();
    } else if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  };

  // Steps definition (titles only)
  const steps = ['Customer Selection', 'Product Selection', 'Order & Payment', 'Confirmation'];

  const calculateTotal = () => {
    const subtotal = selectedProducts.reduce((sum, product) =>
      sum + (product.calculated_price * product.quantity), 0
    );
    const tax = subtotal * 0.03;
    return subtotal + tax;
  };

  const cartTotals = getCartTotals();

  return (
    <div className="p-6 space-y-6">
      {/* Header with Cart */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-primary">Sales Workflow</h1>
          <p className="text-gray-600">Complete jewelry sales process</p>
        </div>

        {/* Cart Button */}
        <button
          onClick={() => setShowCart(true)}
          className="relative px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 whitespace-nowrap"
        >
          <i className="ri-shopping-cart-line mr-2"></i>
          Cart
          {cartItems.length > 0 && (
            <span className="absolute -top-2 -right-2 w-6 h-6 bg-red-5 00 text-white text-xs rounded-full flex items-center justify-center">
              {cartTotals.totalItems}
            </span>
          )}
        </button>
      </div>

      {/* Progress Steps */}
      <div className="bg-white rounded-lg p-6 border">
        <div className="flex items-center justify-between mb-6">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                index + 1 <= currentStep
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {index + 1 <= currentStep ? (
                  index + 1 < currentStep ? <i className="ri-check-line"></i> : index + 1
                ) : index + 1}
              </div>
              <span className={`ml-3 text-sm ${
                index + 1 <= currentStep ? 'text-primary font-medium' : 'text-gray-500'
              }`}>
                {step}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-20 h-0.5 mx-6 ${
                  index + 1 < currentStep ? 'bg-primary' : 'bg-gray-200'
                }`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        {currentStep === 1 && (
          <CustomerSearch
            onCustomerSelect={handleCustomerSelect}
            selectedCustomer={selectedCustomer}
          />
        )}

        {currentStep === 2 && (
          <ProductSearch
            onProductSelect={handleProductSelect}
            customerId={selectedCustomer?.id}
          />
        )}

        {currentStep === 3 && selectedCustomer && (
          <SalesOrderBooking
            customerId={selectedCustomer.id}
            onOrderComplete={handleOrderComplete}
          />
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6 pt-6 border-t">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Previous
          </button>

          <div className="flex space-x-4">
            {cartItems.length > 0 && (
              <button
                onClick={() => setShowCart(true)}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                View Cart ({cartTotals.totalItems})
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Next
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Shopping Cart Modal */}
      {showCart && (
        <ShoppingCart
          customerId={selectedCustomer?.id}
          onCheckout={handleCartCheckout}
          onClose={() => setShowCart(false)}
        />
      )}

      {/* Checkout Flow Modal */}
      {showCheckout && checkoutData && selectedCustomer && (
        <CheckoutFlow
          cartItems={checkoutData.items}
          totals={checkoutData.totals}
          customerId={selectedCustomer.id}
          onComplete={handleCheckoutComplete}
          onCancel={() => {
            setShowCheckout(false);
            setCheckoutData(null);
          }}
        />
      )}
    </div>
  );
};

export default SalesWorkflow;
